import type { ActivityItem, ActivityKey } from '../../activity-journal';
import {
    isActiveActivityJobStatus,
    isCompletedActivityJobStatus,
    isFailedActivityJobStatus,
    isSkippedActivityJobStatus,
    presentationActivityJob,
} from '../../activity-journal/job';
import {
    activityIntentFor,
    canDismissActivity,
    hasActivityWarning,
} from '../../activity-journal/policy';
import {
    activityBuildingContext,
    type ActivityBuildingContext,
    type ActivityBuildingDisplay,
} from '../building-display';
import {
    activityIconContext,
    activityProgress,
    activityStageIcon,
    activityStatus,
    activityUpdatedLabel,
    processingActivityTitle,
    toActivityAction,
} from '../card-policy';
import type {
    DocumentBatchActivityCardView,
    DocumentBatchStage,
    DocumentBatchStageView,
    ActivityProgressIndicatorView,
} from '../models';
import { compareItemsNewestFirst, compareItemsOldestFirst } from './item-order';

const DOCUMENT_BATCH_JOB_TYPES = new Set([
    'DocumentImport',
    'SharePointImport',
    'DocumentClassification',
    'DocumentIntelligence',
]);

export function isDocumentBatchItem(item: ActivityItem): boolean {
    return item.jobs.some((job) => DOCUMENT_BATCH_JOB_TYPES.has(job.jobType));
}

export function toDocumentBatchView(
    batchIdentity: string,
    items: readonly ActivityItem[],
    now: number,
    buildingLookups: ReadonlyMap<string, ActivityBuildingDisplay>
): DocumentBatchActivityCardView {
    const sortedItems = [...items].sort(compareItemsNewestFirst);
    const intake = sortedItems.find(
        (item) => presentationActivityJob(item).stage === 'intake'
    );
    const classification = sortedItems.find(
        (item) => presentationActivityJob(item).stage === 'classification'
    );
    const observedIntake = sortedItems.find(
        (item) => item.observedIntake
    )?.observedIntake;
    const primaryItem = intake ?? classification ?? sortedItems[0];
    if (!primaryItem) {
        throw new Error('Document batches require at least one activity item.');
    }

    const combinedItem = combineBatchItems(
        `batch:${batchIdentity}` as ActivityKey,
        sortedItems
    );
    const building = activityBuildingContext(combinedItem, buildingLookups);
    const primaryJob = presentationActivityJob(primaryItem);
    const unavailable = combinedItem.availability === 'unavailable';
    const available = combinedItem.availability === 'available';
    const inferredUpload =
        available &&
        intake === undefined &&
        observedIntake === undefined &&
        classification !== undefined;
    const title = unavailable
        ? 'Document processing status unavailable'
        : inferredUpload
          ? 'Documents uploaded'
          : processingActivityTitle(primaryItem, primaryJob);
    const eventAtUtc = inferredUpload
        ? primaryItem.createdAtUtc
        : primaryItem.updatedAtUtc;
    const actionItem = unavailable
        ? undefined
        : sortedItems.find(
              (item) => activityIntentFor(item)?.type === 'view-receipt'
          );

    return {
        kind: 'document-batch',
        id: combinedItem.key,
        itemIds: sortedItems.map((item) => item.key),
        actionItemId: actionItem?.key ?? null,
        title,
        accessibleLabel: activityAccessibleLabel(
            combinedItem,
            title,
            building,
            now
        ),
        ...buildingViewState(building),
        updatedAtUtc: combinedItem.updatedAtUtc,
        eventLabel: activityUpdatedLabel(eventAtUtc, now),
        message: inferredUpload
            ? 'Direct upload completed.'
            : (primaryJob.message ??
              observedIntake?.message ??
              (observedIntake ? 'SharePoint import observed.' : null)),
        icon: inferredUpload
            ? 'lucideUpload'
            : activityStageIcon(primaryJob.stage),
        iconContext: unavailable
            ? { family: 'destructive' }
            : inferredUpload
              ? { family: 'success' }
              : activityIconContext(primaryItem, 'intake'),
        statusLabel: activityStatus(combinedItem).label,
        progress:
            unavailable || inferredUpload
                ? null
                : activityProgress(primaryItem, primaryJob, title, {
                      countLabel: activityCountLabel(primaryJob),
                  }),
        primaryAction: actionItem
            ? toActivityAction(activityIntentFor(actionItem), building.label)
            : null,
        dismissible: sortedItems.every(canDismissActivity),
        stages: toDocumentBatchStages(sortedItems),
    };
}

function combineBatchItems(
    key: ActivityKey,
    items: readonly ActivityItem[]
): ActivityItem {
    const newest = [...items].sort(compareItemsNewestFirst)[0];
    const oldest = [...items].sort(compareItemsOldestFirst)[0];
    if (!newest || !oldest) {
        throw new Error('Document batches require at least one activity item.');
    }

    return {
        key,
        correlationId: newest.correlationId,
        jobs: items.flatMap((item) => item.jobs),
        observedJobIds: items.flatMap((item) => item.observedJobIds ?? []),
        createdAtUtc: oldest.createdAtUtc,
        updatedAtUtc: newest.updatedAtUtc,
        seen: items.every((item) => item.seen),
        actedOn: items.every((item) => item.actedOn),
        availability: items.some((item) => item.availability === 'unavailable')
            ? 'unavailable'
            : items.some((item) => item.availability === 'stale')
              ? 'stale'
              : 'available',
        identityQuarantined: items.some(
            (item) => item.identityQuarantined === true
        ),
    };
}

function toDocumentBatchStages(
    items: readonly ActivityItem[]
): readonly DocumentBatchStageView[] {
    return (['classification', 'enrichment'] as const).flatMap((stage) => {
        const item = items.find(
            (candidate) => presentationActivityJob(candidate).stage === stage
        );
        return item ? [toDocumentBatchStage(item, stage)] : [];
    });
}

function toDocumentBatchStage(
    item: ActivityItem,
    stage: DocumentBatchStage
): DocumentBatchStageView {
    const job = presentationActivityJob(item);
    const activeTitle =
        stage === 'classification' ? 'Classifying' : 'Enriching';
    const terminalTitle =
        stage === 'classification' ? 'Classified' : 'Enriched';
    const neutralTitle =
        stage === 'classification' ? 'Classification' : 'Enrichment';
    const active =
        item.availability === 'available' &&
        item.jobs.some((candidate) =>
            isActiveActivityJobStatus(candidate.status)
        );
    const completed =
        item.availability === 'available' &&
        item.jobs.length > 0 &&
        item.jobs.every((candidate) =>
            isCompletedActivityJobStatus(candidate.status)
        );
    const title = active
        ? activeTitle
        : completed
          ? terminalTitle
          : neutralTitle;
    const progress = toStageProgress(
        activityProgress(item, job, activeTitle, {
            countLabel: activityCountLabel(job),
        })
    );
    const statusLabel = batchStageStatusLabel(item);

    return {
        id: item.key,
        stage,
        title,
        accessibleLabel: `${title}; ${progress?.ariaLabel ?? statusLabel ?? 'status unavailable'}`,
        icon: stage === 'classification' ? 'lucideShapes' : 'lucideSparkles',
        iconContext: activityIconContext(item, stage),
        statusLabel,
        progress,
    };
}

function toStageProgress(
    progress: ActivityProgressIndicatorView | null
): ActivityProgressIndicatorView | null {
    if (!progress) return null;

    return progress.mode === 'determinate'
        ? { ...progress, ariaLabel: `${progress.label} complete` }
        : { ...progress, ariaLabel: 'In progress' };
}

function batchStageStatusLabel(item: ActivityItem): string | null {
    if (item.availability === 'unavailable') return 'Unavailable';
    if (item.availability === 'stale') return null;
    if (item.jobs.some((job) => isFailedActivityJobStatus(job.status))) {
        return 'Failed';
    }
    if (hasActivityWarning(item)) return 'Partial';
    if (item.jobs.some((job) => isActiveActivityJobStatus(job.status))) {
        return null;
    }
    if (item.jobs.some((job) => isSkippedActivityJobStatus(job.status))) {
        return 'Skipped';
    }
    if (
        item.jobs.length > 0 &&
        item.jobs.every((job) => isCompletedActivityJobStatus(job.status))
    ) {
        return 'Complete';
    }

    return null;
}

function activityCountLabel(
    representative: ActivityItem['jobs'][number]
): string | null {
    const { completedItems, totalItems } = representative;
    return typeof completedItems === 'number' &&
        typeof totalItems === 'number' &&
        Number.isInteger(completedItems) &&
        Number.isInteger(totalItems) &&
        completedItems >= 0 &&
        completedItems <= totalItems
        ? `${completedItems} of ${totalItems}`
        : null;
}

function activityAccessibleLabel(
    item: ActivityItem,
    title: string,
    building: ActivityBuildingContext,
    now: number
): string {
    return `${title} for ${building.label}, ${activityUpdatedLabel(item.updatedAtUtc, now)}`;
}

function buildingViewState(building: ActivityBuildingContext) {
    return {
        buildingPublicId: building.buildingPublicId,
        buildingLabel: building.label,
        buildingAddress: building.address,
    };
}
